import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { SlidersHorizontal, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Drop-in panel that renders whatever custom fields an admin has defined for
 * `entityType` (see Settings → Custom Fields) against `entityId`, and saves
 * edits on blur. Renders nothing if no fields are defined for this entity
 * type, so it's safe to embed unconditionally on any entity page.
 */
export default function CustomFieldsPanel({ entityType, entityId }) {
  const [definitions, setDefinitions] = useState([]);
  const [values, setValues] = useState({});
  const [draft, setDraft] = useState({});
  const [expanded, setExpanded] = useState(true);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/custom-fields/panel/${entityType}/${entityId}`);
      setDefinitions(data.definitions || []);
      setValues(data.values || {});
      setDraft(data.values || {});
    } catch {
      // fail silent — a missing custom-fields panel shouldn't break the host page
    } finally {
      setLoaded(true);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    if (entityType && entityId) load();
  }, [entityType, entityId, load]);

  const save = async (fieldId, rawValue) => {
    if (values[fieldId] === rawValue || (values[fieldId] == null && rawValue === "")) return;
    try {
      const { data } = await api.put(`/custom-fields/values/${entityType}/${entityId}`, { values: { [fieldId]: rawValue } });
      setValues(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save field");
      setDraft(values); // revert
    }
  };

  if (!loaded || definitions.length === 0) return null;

  return (
    <div className="border border-[#1F2937] bg-[#0D1117]" data-testid="custom-fields-panel">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full h-11 flex items-center justify-between px-4 text-xs uppercase tracking-[0.25em] text-slate-400 hover:text-white hover:bg-[#111827] transition-colors"
      >
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-3.5 h-3.5 text-[#00F5D4]" />
          Custom fields · {definitions.length}
        </div>
        {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {expanded && (
        <div className="border-t border-[#1F2937] p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {definitions.map((f) => (
            <div key={f.field_id}>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-1 block">
                {f.label}{f.is_required && <span className="text-[#FF007A]"> *</span>}
              </label>
              {f.field_type === "select" ? (
                <select
                  value={draft[f.field_id] ?? ""}
                  onChange={(e) => { setDraft((d) => ({ ...d, [f.field_id]: e.target.value })); save(f.field_id, e.target.value); }}
                  className="w-full h-9 rounded-none border border-[#374151] bg-[#030712] text-white px-2 text-sm"
                  data-testid={`cf-input-${f.key}`}
                >
                  <option value="">—</option>
                  {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.field_type === "checkbox" ? (
                <input
                  type="checkbox"
                  checked={Boolean(draft[f.field_id])}
                  onChange={(e) => { setDraft((d) => ({ ...d, [f.field_id]: e.target.checked })); save(f.field_id, e.target.checked); }}
                  className="accent-[#00F5D4] w-4 h-4"
                  data-testid={`cf-input-${f.key}`}
                />
              ) : (
                <input
                  type={f.field_type === "number" ? "number" : f.field_type === "date" ? "date" : f.field_type === "url" ? "url" : "text"}
                  value={draft[f.field_id] ?? ""}
                  onChange={(e) => setDraft((d) => ({ ...d, [f.field_id]: e.target.value }))}
                  onBlur={(e) => save(f.field_id, e.target.value)}
                  className="w-full h-9 rounded-none border border-[#374151] bg-[#030712] text-white px-2 text-sm"
                  data-testid={`cf-input-${f.key}`}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
